import React, { useCallback, useRef, useState } from 'react';
import { Autocomplete, type AutocompleteHandle } from '../../Autocomplete';
import { useSearchContextOptional } from '../../../contexts/SearchContext';
import { primaryNavHrefByKey, type PrimaryNavLocale } from '../primary-nav-links';
import { navIndexMessages } from './nav-index-messages';
import { SearchFiltersChip, SearchWhereChip } from './SearchBarParts';
import { ClosedSearchBar } from './ClosedSearchBar';
import { searchBarMessages } from './search-bar-messages';
import { useMenuPulse } from '../menu/useMenuPulse';
import { usePageContext } from 'vike-react/usePageContext';
import type { MenuPulse } from '../../../server/routes/menu-pulse';
import type { IntlShape } from 'react-intl';
import { useSearchPanelComponent } from '../../search-panel/search-panel-lazy';
import type { SearchPanelScope } from '../../search-panel/types';
import { useRecentSearchesForPanel } from '../../../utils/search-panel/use-recent-searches-for-panel';

/** Same key AllFiltersModal already reads its initial tab from (see DesktopFilterCategories'
 * handleLocationClick); writing it before navigating to the jobs route makes the modal open
 * straight to Location once it mounts there, same as clicking the SRP's own location button. */
const ALL_FILTERS_OPEN_SECTION_KEY = 'allFiltersModal.openSection';
/** One-shot flag: the SRP opens the filters modal once on arrival, then clears it (SearchWrapper). */
export const ALL_FILTERS_AUTO_OPEN_KEY = 'allFiltersModal.autoOpen';

function navigateToFiltersModal(locale: PrimaryNavLocale, section: 'location' | 'job'): void {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(ALL_FILTERS_OPEN_SECTION_KEY, section);
      window.localStorage.setItem(ALL_FILTERS_AUTO_OPEN_KEY, '1');
    } catch {
      // Private browsing / storage disabled: the modal just won't default to Location, no crash.
    }
  }
  window.location.assign(primaryNavHrefByKey('jobs', locale));
}

type FormatMessage = IntlShape['formatMessage'];
type LauncherSizeVariant = 'compact40' | 'compact44';

interface SearchLauncherProps {
  locale: PrimaryNavLocale;
  formatMessage: FormatMessage;
  /** 'compact40' (default): the desktop row-one 40px control. 'compact44': the mobile bottom
   * nav's 44px minimum tap target (TEC navbar redesign Phase 2 item 2.7). */
  sizeVariant?: LauncherSizeVariant;
  /** Facet-suggestions dropdown opens upward instead of down, same as MobileBottomNav's own
   * Autocomplete usage, since that bar is fixed to the bottom of the viewport. */
  popUp?: boolean;
  onOpen?: () => void;
  /** MobileBottomNav's own on-SRP search field has never had a fused location segment (location
   * lives behind the separate filters button there); off-SRP it reuses this component but keeps
   * that same shape rather than introducing a new location button the bottom bar never had. */
  showLocationSegment?: boolean;
}

/**
 * Exported (TEC navbar redesign Phase 4 item 1) so ResourceNavbar.tsx, which has no
 * SearchProvider/FacetsContext either, can render the exact same location button server-side:
 * this component takes no hooks and no context, only props, so it is safe in a prerendered page.
 */
export const LocationSegment: React.FC<{ locale: PrimaryNavLocale; formatMessage: FormatMessage; sizeVariant: LauncherSizeVariant }> = ({
  locale,
  formatMessage,
  sizeVariant,
}) => (
  // Sage search bar (2026-09-28): the Where chip. Off the SRP no location is ever applied, so
  // it always reads "Anywhere"; a click opens the jobs page's filters on its Location tab.
  <SearchWhereChip
    onClick={() => navigateToFiltersModal(locale, 'location')}
    label={formatMessage(searchBarMessages.whereLabel)}
    value={formatMessage({
      id: 'search.refinements.locationAnywhere',
      defaultMessage: 'Anywhere',
      description: "Search panel's own location pill label when no location filter is set (remoteness does not count)",
    })}
    ariaLabel={formatMessage({
      id: 'search.refinements.location',
      defaultMessage: 'Location',
      description: 'Filter label for location selection in search refinements',
    })}
    sizeVariant={sizeVariant}
  />
);

/** Off-SRP filters chip: opens the jobs page with the filters modal already open on its job
 * section (the Where chip beside it covers location). */
const FiltersSegment: React.FC<{ locale: PrimaryNavLocale; formatMessage: FormatMessage }> = ({ locale, formatMessage }) => (
  <SearchFiltersChip
    onClick={() => navigateToFiltersModal(locale, 'job')}
    ariaLabel={formatMessage({
      id: 'search.refinements.advancedFilters.aria',
      defaultMessage: 'Advanced Filters',
      description: 'Accessibility label for the button that opens the full filters modal',
    })}
  />
);

/** All open jobs from the navbar's shared pulse (SSR-seeded where the page has one): the count
 * button's number off the SRP, where nothing is narrowed yet. */
function useOpenJobsCount(locale: PrimaryNavLocale): number | null {
  const pageContext = usePageContext() as { navPulse?: MenuPulse | null } | undefined;
  const pulse = useMenuPulse(locale, true, pageContext?.navPulse ?? null);
  return pulse?.openJobs ?? null;
}

/** Non-SRP variant: a real SearchProvider is mounted (most pages), so the exact same Autocomplete
 * used on the SRP renders here too, with onSubmitNavigate wired to a real page navigation
 * instead of the SRP's in-place pushState. */
const LiveLauncher: React.FC<Required<Pick<SearchLauncherProps, 'locale' | 'formatMessage' | 'sizeVariant' | 'popUp' | 'showLocationSegment'>> & { onOpen?: () => void }> = ({
  locale,
  formatMessage,
  sizeVariant,
  popUp,
  onOpen,
  showLocationSegment,
}) => {
  const autocompleteRef = useRef<AutocompleteHandle>(null);
  const openJobs = useOpenJobsCount(locale);
  // Full document loads, not vike's client-side navigate(): the SRP is rendered by its own
  // client island (pages/index/srp.client.ts) and does not mount through a client-side route
  // transition from another page; navigate() only swapped the URL and left the page in place.
  const handleSubmitNavigate = useCallback((url: string) => {
    window.location.assign(url);
  }, []);
  // Coordinator review round 1/2, bug 14: Autocomplete's own bar is the whole control and expands
  // in place; a separate LocationSegment sibling here would either sit outside the expanding card
  // or force it to cover a wider area than its own measured anchor, so it's dropped in favour of
  // Autocomplete's own `locationSegment` prop (round 3, bug 5): beside its own closed bar, then
  // inside SearchPanel's header once open.
  return (
    <Autocomplete
      ref={autocompleteRef}
      attached={showLocationSegment}
      popUp={popUp}
      onOpen={onOpen}
      sizeVariant={sizeVariant}
      placeholder={formatMessage(navIndexMessages.searchPlaceholder)}
      onSubmitNavigate={handleSubmitNavigate}
      className="min-w-0 flex-1 max-w-[720px]"
      locationSegment={showLocationSegment ? <LocationSegment locale={locale} formatMessage={formatMessage} sizeVariant={sizeVariant} /> : undefined}
      filtersSegment={showLocationSegment ? <FiltersSegment locale={locale} formatMessage={formatMessage} /> : undefined}
      count={openJobs}
      countTotal={openJobs}
    />
  );
};

/** MinimalChrome pages (resources, about, for-employers, locations, authors, tax-iceberg) mount
 * no SearchProvider, so the live Autocomplete would throw. This mounts the same SearchPanel every
 * other host uses, with a `window.location.assign` navigate adapter instead of pushIndexUiState. */
const FallbackLauncher: React.FC<
  Required<Pick<SearchLauncherProps, 'locale' | 'formatMessage' | 'sizeVariant' | 'showLocationSegment'>>
> = ({ locale, formatMessage, sizeVariant, showLocationSegment }) => {
  const panelRecentSearches = useRecentSearchesForPanel();
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  // Coordinator review round 4, bug 4: closing must reverse the clip like every other host, not
  // unmount instantly. `closingSearchPanel` keeps the card mounted (and the bar hidden) through
  // the reverse animation; onCloseAnimationEnd is what actually clears isSearchPanelOpen.
  const [closingSearchPanel, setClosingSearchPanel] = useState(false);
  // Priority bug fix (same rule as Autocomplete.tsx): focus alone never opens the panel; only a
  // pointer click on the bar, or Enter/Space/ArrowDown/a printable character while the bar itself
  // has focus. The printable-character case seeds that character as the panel's own first
  // keystroke via `panelScope.query`, since SearchPanel/useSearchPanel reads it once, on mount.
  const [openSeedQuery, setOpenSeedQuery] = useState<string | null>(null);
  const searchPanelContainerRef = useRef<HTMLDivElement>(null);
  const searchPanelBarRef = useRef<HTMLDivElement>(null);
  const closeSearchPanel = useCallback(() => setClosingSearchPanel(true), []);
  // Loaded on demand (search-panel-lazy.ts); the bar stays showing until the code is here.
  const SearchPanel = useSearchPanelComponent(isSearchPanelOpen);
  const panelShown = (isSearchPanelOpen || closingSearchPanel) && SearchPanel !== null;
  const openJobs = useOpenJobsCount(locale);

  const panelScope: SearchPanelScope = { query: openSeedQuery ?? '', filters: {}, locale: locale as SearchPanelScope['locale'] };
  const navigateAdapter = (url: string) => window.location.assign(url);
  return (
    <div ref={searchPanelContainerRef} className="relative min-w-0 flex-1 max-w-[720px]">
      {/* The bar stays mounted (visibility: hidden, never display:none) so SearchPanel can
       * measure its rect as the expand animation's anchor, and so it never visibly coexists with
       * the open card. Same ClosedSearchBar the Autocomplete hosts render. */}
      <ClosedSearchBar
        shellRef={searchPanelBarRef}
        hidden={panelShown}
        placeholder={formatMessage(navIndexMessages.searchPlaceholder)}
        onOpen={(seed) => {
          // Keys typed while the panel's code is still loading land on the bar again: add them.
          if (isSearchPanelOpen && !SearchPanel) {
            if (seed) setOpenSeedQuery((prev) => (prev ?? '') + seed);
          } else {
            setOpenSeedQuery(seed ?? null);
          }
          setIsSearchPanelOpen(true);
        }}
        locationSegment={showLocationSegment ? <LocationSegment locale={locale} formatMessage={formatMessage} sizeVariant={sizeVariant} /> : undefined}
        filtersSegment={showLocationSegment ? <FiltersSegment locale={locale} formatMessage={formatMessage} /> : undefined}
        count={openJobs}
        countTotal={openJobs}
        sizeVariant={sizeVariant}
      />
      {panelShown && SearchPanel && (
        <div className="absolute left-0 right-0 top-0 z-(--jc-z-dropdown)">
          <SearchPanel
            variant="dropdown"
            scope={panelScope}
            onGo={navigateAdapter}
            onApplyFilters={navigateAdapter}
            recentSearches={panelRecentSearches}
            anchorRef={searchPanelBarRef}
            onClose={closeSearchPanel}
            closing={closingSearchPanel}
            onCloseAnimationEnd={() => {
              setIsSearchPanelOpen(false);
              setClosingSearchPanel(false);
              setOpenSeedQuery(null);
            }}
            autoFocus
            placeholder={formatMessage(navIndexMessages.searchPlaceholder)}
            // Coordinator review round 6: same defensive wrapper as Autocomplete.tsx's own
            // trailingHeaderSlot (onMouseDown preventDefault so a click here never blurs
            // whatever has focus first). FallbackLauncher has no outside-click/blur close wired
            // up today (only the header's own X button closes it), so bug 1 (the pill closing
            // the panel before its own click fires) cannot currently reproduce here, but this
            // keeps the two hosts' location pills behaving identically if that ever changes.
            trailingHeaderSlot={
              showLocationSegment ? (
                <div className="contents" onMouseDown={(e) => e.preventDefault()}>
                  {/* Same as Autocomplete.tsx: the closed bar's filters chip is hidden while the
                   * panel is open, so the panel carries its own way into the filters. */}
                  <FiltersSegment locale={locale} formatMessage={formatMessage} />
                  <LocationSegment locale={locale} formatMessage={formatMessage} sizeVariant={sizeVariant} />
                </div>
              ) : undefined
            }
          />
        </div>
      )}
    </div>
  );
};

/**
 * Row-one search bar for the navbar live-index redesign (TEC navbar redesign Phase 1). On the
 * SRP, DesktopFilterCategories keeps using SearchNavbar's own live Autocomplete directly (that
 * behaviour is unchanged by this component). Everywhere else, this picks between the live
 * Autocomplete (when a SearchProvider is mounted, i.e. every page except MinimalChrome's
 * resources/about/for-employers/locations/authors/tax-iceberg) and a SearchPanel-backed fallback.
 *
 * Phase 2 item 2.7 reuses this same component (sizeVariant="compact44", popUp) for
 * MobileBottomNav's search field on non-SRP pages, so there is exactly one
 * "has a SearchProvider or not" branch in the whole redesign, not two.
 */
export const SearchLauncher: React.FC<SearchLauncherProps> = ({
  locale,
  formatMessage,
  sizeVariant = 'compact40',
  popUp = false,
  onOpen,
  showLocationSegment = true,
}) => {
  const hasSearchContext = useSearchContextOptional() !== undefined;
  return hasSearchContext ? (
    <LiveLauncher
      locale={locale}
      formatMessage={formatMessage}
      sizeVariant={sizeVariant}
      popUp={popUp}
      onOpen={onOpen}
      showLocationSegment={showLocationSegment}
    />
  ) : (
    <FallbackLauncher locale={locale} formatMessage={formatMessage} sizeVariant={sizeVariant} showLocationSegment={showLocationSegment} />
  );
};
